/**
 * The editing model for a number that formats as it is typed.
 *
 * The draft has two spellings. The RAW spelling is `-?\d*(\.\d*)?`: a sign,
 * digits, one canonical `.` decimal — no separators. The FORMATTED spelling
 * is what the input shows: the raw digits grouped with the locale's group
 * separator and the locale's decimal glyph. Every edit is computed in raw
 * space and projected back, and the caret travels as a count of raw
 * characters — so deleting across a group separator removes the digit beside
 * it (the separator is derived, deleting it alone would regroup back at once
 * and read as a stuck key), and a digit inserted ahead of the decimal point
 * regroups the integer part while the caret stays beside the digit it was
 * next to.
 *
 * An edit that cannot produce a valid draft is REJECTED: the previous text
 * comes back untouched with the reason named, so the host can paint the
 * error and explain it. A valid draft may still be an incomplete number
 * (`""`, `-`, `322.`); `numberDraftValue` reads those as `null`/`322`.
 */

export type NumberDraftLocale = {
  /** The thousands separator glyph. */
  group: string;
  /** The decimal separator glyph. */
  decimal: string;
};

export type NumberDraftOptions = {
  locale: NumberDraftLocale;
  /** Most digits allowed after the decimal separator. 0 forbids one. */
  maxFractionDigits: number;
  /** Most digits allowed before it. Default 15 — the significant digits a
   *  double carries exactly; a value that ALSO carries decimals at that
   *  width settles to the nearest representable double. */
  maxIntegerDigits?: number | undefined;
  /** Whether a leading minus sign is accepted. Default true. */
  allowNegative?: boolean | undefined;
  /**
   * What a decimal past `maxFractionDigits` does. `"refuse"` (default) turns
   * the keystroke down — the cap is a hard rule, like a currency's minor
   * unit. `"round"` lets the draft carry more while typing (up to a double's
   * useful precision) and rounds to the cap when the draft settles — for a
   * quantity where the cap is a display preference, not a rule.
   */
  fractionOverflow?: "refuse" | "round" | undefined;
  /** Whether the integer digits group with the locale's separator as they
   *  are typed (`12,345`). Default true. Off writes the bare digits — for an
   *  identifier or a year, where a separator misreads as an amount. */
  grouping?: boolean | undefined;
};

export type NumberDraftRejection =
  /** A glyph that is not a digit, the decimal or a minus sign. */
  | "character"
  /** A minus sign where one is already present or where none is allowed. */
  | "sign"
  /** A second decimal separator, or one where no decimals are allowed. */
  | "decimal"
  /** More fraction digits than the cap. */
  | "fraction-digits"
  /** More integer digits than the cap. */
  | "integer-digits";

export type NumberDraftEdit = {
  text: string;
  caret: number;
  rejected: NumberDraftRejection | null;
};

export type NumberDraftInput =
  | { type: "insert"; text: string }
  | { type: "delete-backward" }
  | { type: "delete-forward" };

const DEFAULT_MAX_INTEGER_DIGITS = 15;
/** Fraction digits a rounding draft may carry before it is refused: past
 *  this a double no longer distinguishes the digits typed. */
const MAX_ROUNDING_FRACTION_DIGITS = 15;
const DEFAULT_LOCALE: NumberDraftLocale = { group: ",", decimal: "." };

const localeCache = new Map<string, NumberDraftLocale>();

/** The group and decimal glyphs a locale writes numbers with, read off
 *  `Intl.NumberFormat`. Latin digits are forced so the glyphs pair with the
 *  ASCII digits the draft keeps. */
export function numberDraftLocale(locale?: string): NumberDraftLocale {
  const key = locale ?? "";
  const cached = localeCache.get(key);
  if (cached) return cached;
  let resolved = DEFAULT_LOCALE;
  try {
    const parts = new Intl.NumberFormat(locale, {
      numberingSystem: "latn",
    }).formatToParts(1234567.5);
    const group = parts.find((part) => part.type === "group")?.value;
    const decimal = parts.find((part) => part.type === "decimal")?.value;
    if (group && decimal) resolved = { group, decimal };
  } catch {
    // An unknown locale tag falls back to the default glyphs.
  }
  localeCache.set(key, resolved);
  return resolved;
}

type RawDraft = { raw: string; caret: number };

/** Formatted → raw. Group glyphs (and any whitespace — some locales group
 *  with a space) are dropped, the decimal glyph becomes `.`, and every other
 *  character is kept so validation can name it. */
function toRaw(text: string, caret: number, locale: NumberDraftLocale): RawDraft {
  let raw = "";
  let rawCaret = 0;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    if (char === locale.group || /\s/.test(char)) continue;
    raw += char === locale.decimal ? "." : char;
    if (index < caret) rawCaret += 1;
  }
  return { raw, caret: rawCaret };
}

function groupDigits(digits: string, group: string): string {
  let grouped = "";
  for (let index = 0; index < digits.length; index += 1) {
    const fromEnd = digits.length - index;
    if (index > 0 && fromEnd % 3 === 0) grouped += group;
    grouped += digits[index];
  }
  return grouped;
}

/** Raw → formatted. Leading zeros on the integer part collapse (`05` → `5`),
 *  the integer digits group (unless grouping is off), and the caret lands
 *  after the same count of raw characters it followed before — which puts
 *  it BEFORE a separator when it sits on a group boundary. */
function fromRaw(
  { raw, caret }: RawDraft,
  options: NumberDraftOptions,
): { text: string; caret: number } {
  const { locale } = options;
  const group = options.grouping === false ? "" : locale.group;
  const sign = raw.startsWith("-") ? "-" : "";
  const body = raw.slice(sign.length);
  const decimalAt = body.indexOf(".");
  const integer = decimalAt === -1 ? body : body.slice(0, decimalAt);
  const fraction = decimalAt === -1 ? null : body.slice(decimalAt + 1);

  const trimmed = integer.replace(/^0+(?=\d)/, "");
  const removed = integer.length - trimmed.length;
  // The removed zeros sat right after the sign; a caret past them moves back
  // by however many it was beyond.
  const removedBeforeCaret = Math.max(
    0,
    Math.min(removed, caret - sign.length),
  );
  const rawCaret = caret - removedBeforeCaret;

  const text =
    sign +
    groupDigits(trimmed, group) +
    (fraction === null ? "" : locale.decimal + fraction);

  let seen = 0;
  let formattedCaret = text.length;
  for (let index = 0; index < text.length; index += 1) {
    if (seen === rawCaret) {
      formattedCaret = index;
      break;
    }
    if (text[index] !== locale.group) seen += 1;
  }
  return { text, caret: formattedCaret };
}

function validateRaw(
  raw: string,
  options: NumberDraftOptions,
): NumberDraftRejection | null {
  if (!/^[-\d.]*$/.test(raw)) return "character";
  const minusCount = raw.split("-").length - 1;
  if (minusCount > 1 || (minusCount === 1 && !raw.startsWith("-"))) {
    return "sign";
  }
  if (minusCount === 1 && options.allowNegative === false) return "sign";
  const decimalCount = raw.split(".").length - 1;
  if (decimalCount > 1) return "decimal";
  if (decimalCount === 1 && options.maxFractionDigits === 0) return "decimal";
  const body = raw.replace(/^-/, "");
  const [integer = "", fraction = ""] = body.split(".");
  const fractionCap =
    options.fractionOverflow === "round"
      ? MAX_ROUNDING_FRACTION_DIGITS
      : options.maxFractionDigits;
  if (fraction.length > fractionCap) return "fraction-digits";
  const integerDigits = integer.replace(/^0+(?=\d)/, "").length;
  if (integerDigits > (options.maxIntegerDigits ?? DEFAULT_MAX_INTEGER_DIGITS)) {
    return "integer-digits";
  }
  return null;
}

/** Pasted text arrives with whatever the source wrote around the number: a
 *  currency symbol, grouping, spaces. Everything that cannot be part of a raw
 *  draft is dropped and the locale's decimal becomes `.`; `null` means
 *  nothing usable was pasted. A lone typed glyph is NOT sanitized — a typed
 *  group separator or letter is a rejection the user should see, not a
 *  silent no-op — except that the locale's decimal glyph becomes `.`, and a
 *  `.` that is NOT the locale's decimal is a stray character (in `de-DE` it
 *  is the group glyph). */
function sanitizeInsert(
  text: string,
  locale: NumberDraftLocale,
): string | null {
  if (text.length <= 1) {
    if (text === locale.decimal) return ".";
    if (text === "." || text === locale.group) return null;
    return text;
  }
  let clean = "";
  for (const char of text) {
    if (char === locale.decimal) clean += ".";
    else if (/\d|-/.test(char)) clean += char;
  }
  return clean === "" ? null : clean;
}

function rejection(
  prev: string,
  caret: number,
  rejected: NumberDraftRejection,
): NumberDraftEdit {
  return { text: prev, caret, rejected };
}

/** Applies one edit to the formatted draft `prev` with the given selection
 *  and returns the next formatted draft with its caret, or the same draft
 *  with the edit rejected. */
export function editNumberDraft(
  prev: string,
  selection: readonly [number, number],
  input: NumberDraftInput,
  options: NumberDraftOptions,
): NumberDraftEdit {
  const { locale } = options;
  const [selectionStart, selectionEnd] = selection;
  const start = toRaw(prev, Math.min(selectionStart, selectionEnd), locale);
  const end = toRaw(prev, Math.max(selectionStart, selectionEnd), locale);
  const raw = start.raw;
  let from = start.caret;
  let to = end.caret;
  let inserted = "";

  if (input.type === "insert") {
    const clean = sanitizeInsert(input.text, locale);
    if (clean === null) return rejection(prev, selectionEnd, "character");
    inserted = clean;
  } else if (from === to) {
    if (input.type === "delete-backward") {
      if (from === 0) return { text: prev, caret: selectionStart, rejected: null };
      from -= 1;
    } else {
      if (to === raw.length) {
        return { text: prev, caret: selectionStart, rejected: null };
      }
      to += 1;
    }
  }

  let nextRaw = raw.slice(0, from) + inserted + raw.slice(to);
  let nextCaret = from + inserted.length;

  // A minus typed anywhere is a request to negate: it moves to the front.
  // A second one is a rejection rather than a toggle, so the field never
  // flips sign under a key the user did not read as "toggle".
  if (inserted === "-" && from > 0) {
    if (raw.startsWith("-")) return rejection(prev, selectionEnd, "sign");
    nextRaw = "-" + raw.slice(0, from) + raw.slice(to);
    nextCaret = from + 1;
  }

  const rejected = validateRaw(nextRaw, options);
  if (rejected) return rejection(prev, selectionEnd, rejected);

  const next = fromRaw({ raw: nextRaw, caret: nextCaret }, options);
  return { ...next, rejected: null };
}

/** Re-formats text that reached the input without going through
 *  `editNumberDraft` (autofill, an IME commit). Invalid text is rejected back
 *  to `prev`. */
export function normalizeNumberDraft(
  text: string,
  caret: number,
  prev: string,
  options: NumberDraftOptions,
): NumberDraftEdit {
  const rawDraft = toRaw(text, caret, options.locale);
  const rejected = validateRaw(rawDraft.raw, options);
  if (rejected) return rejection(prev, prev.length, rejected);
  return { ...fromRaw(rawDraft, options), rejected: null };
}

/** The number a draft stands for: `null` for an empty or sign-only or
 *  decimal-only draft, otherwise the parsed value (`322.` → 322). */
export function numberDraftValue(
  text: string,
  options: NumberDraftOptions,
): number | null {
  const { raw } = toRaw(text, 0, options.locale);
  if (!/\d/.test(raw)) return null;
  const parsed = Number(raw.endsWith(".") ? raw.slice(0, -1) : raw);
  if (!Number.isFinite(parsed)) return null;
  // `-0` is a sign with no amount behind it.
  return parsed === 0 ? 0 : parsed;
}

/** Rounds the raw draft's DIGITS half away from zero at `places` decimals.
 *  The digits are what the user typed, so this is exact where `toFixed`
 *  is not: `3.635` is `3.63499…` as a double and `toFixed(2)` gives `3.63`. */
function roundRawDigits(raw: string, places: number): string {
  const sign = raw.startsWith("-") ? "-" : "";
  const body = raw.slice(sign.length);
  const [integer = "", fraction = ""] = body.split(".");
  if (fraction.length <= places) return raw;
  const kept = fraction.slice(0, places);
  const roundUp = Number(fraction[places]) >= 5;
  let digits = (BigInt(integer || "0") * 10n ** BigInt(places) + BigInt(kept || "0")).toString();
  if (roundUp) digits = (BigInt(digits) + 1n).toString();
  digits = digits.padStart(places + 1, "0");
  const cut = digits.length - places;
  const rounded = places === 0 ? digits : `${digits.slice(0, cut)}.${digits.slice(cut)}`;
  return sign + rounded;
}

/** The number a draft settles to on blur: its value rounded to
 *  `maxFractionDigits` — a no-op unless `fractionOverflow` is `"round"`,
 *  since a refusing draft never carries more. */
export function settleNumberDraft(
  text: string,
  options: NumberDraftOptions,
): number | null {
  const { raw } = toRaw(text, 0, options.locale);
  if (!/\d/.test(raw)) return null;
  const parsed = Number(roundRawDigits(raw.replace(/\.$/, ""), options.maxFractionDigits));
  if (!Number.isFinite(parsed)) return null;
  return parsed === 0 ? 0 : parsed;
}

/** The formatted draft for a settled number: grouped (unless grouping is
 *  off), with only the decimals the value carries (`3.3`, not `3.30`),
 *  rounded to `maxFractionDigits`. */
export function formatNumberDraft(
  value: number,
  options: NumberDraftOptions,
): string {
  const fixed = value.toFixed(options.maxFractionDigits);
  const raw = fixed.includes(".")
    ? fixed.replace(/\.?0+$/, "")
    : fixed;
  return fromRaw({ raw: raw === "-0" ? "0" : raw, caret: 0 }, options).text;
}

/** What to tell the user about a rejected edit. The bubble sits on the
 *  value itself, so the field is already implied: two to four words. */
export function numberDraftRejectionMessage(
  rejected: NumberDraftRejection,
  options: NumberDraftOptions,
): string {
  switch (rejected) {
    case "character":
      return "Numbers only";
    case "sign":
      return options.allowNegative === false ? "No negatives" : "Already negative";
    case "decimal":
      return options.maxFractionDigits === 0
        ? "Whole numbers only"
        : "One decimal point only";
    case "fraction-digits": {
      const digits =
        options.fractionOverflow === "round"
          ? MAX_ROUNDING_FRACTION_DIGITS
          : options.maxFractionDigits;
      return `Max ${digits} decimal${digits === 1 ? "" : "s"}`;
    }
    case "integer-digits":
      return `Max ${options.maxIntegerDigits ?? DEFAULT_MAX_INTEGER_DIGITS} digits`;
  }
}
